/**
 * Utility functions for formatting data display
 */

/**
 * Parse datetime string da API como UTC.
 * A API retorna datetimes naive (sem timezone) que são UTC.
 * JS interpreta strings sem timezone como horário local, causando offset errado.
 */
export function parseApiDate(date: Date | string): Date {
  if (date instanceof Date) return date
  // Se a string não tem indicador de timezone, adiciona 'Z' (UTC)
  if (date && !date.endsWith('Z') && !date.includes('+') && !/\d{2}-\d{2}:\d{2}$/.test(date)) {
    return new Date(`${date}Z`)
  }
  return new Date(date)
}

/**
 * Format bytes to human readable format
 * @param bytes - Number of bytes
 * @param decimals - Number of decimal places (default: 2)
 * @returns Formatted string with appropriate unit
 */
export function formatBytes(bytes: number, decimals: number = 2): string {
  if (bytes === 0) return '0 B'

  const k = 1024
  const dm = decimals < 0 ? 0 : decimals
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']

  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return `${Number.parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`
}

/**
 * Format number with thousands separator
 * @param num - Number to format
 * @param decimals - Number of decimal places (default: 0)
 * @returns Formatted number string
 */
export function formatNumber(num: number, decimals: number = 0): string {
  return new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(num)
}

/**
 * Format percentage with specified decimal places
 * @param value - Value to format as percentage (0-100)
 * @param decimals - Number of decimal places (default: 1)
 * @returns Formatted percentage string
 */
export function formatPercentage(value: number, decimals: number = 1): string {
  return `${formatNumber(value, decimals)}%`
}

/**
 * Format date to Brazilian format
 * @param date - Date to format
 * @param includeTime - Whether to include time (default: true)
 * @returns Formatted date string
 */
export function formatDate(date: Date | string, includeTime: boolean = true): string {
  const dateObj = parseApiDate(date)

  if (includeTime) {
    return dateObj.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  return dateObj.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
}

/**
 * Dia de CALENDÁRIO gravado como meia-noite UTC (início/fim de contrato, `vigente_desde` de uma
 * revisão): "01/10/2026" em qualquer fuso. `formatDate` converte para o fuso do navegador e, no
 * Brasil, mostraria "30/09/2026" — o dia anterior ao que a cobrança conta (#1091 F3).
 */
export function formatCalendarDate(date: Date | string): string {
  return parseApiDate(date).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: 'UTC',
  })
}

/** Competência de cobrança "2026-09" → "09/2026" (o que não tiver esse formato volta como veio). */
export function rotuloDaCompetencia(competencia: string): string {
  const [ano, mes] = competencia.split('-')
  return mes && ano ? `${mes}/${ano}` : competencia
}

/**
 * Format relative time (e.g., "2 hours ago")
 * @param date - Date to format
 * @returns Relative time string
 */
export function formatRelativeTime(date: Date | string): string {
  const dateObj = parseApiDate(date)
  const now = new Date()
  const diffInSeconds = Math.floor((now.getTime() - dateObj.getTime()) / 1000)

  // Handle future dates or very recent dates
  if (Math.abs(diffInSeconds) < 60) {
    return diffInSeconds <= 0 ? 'agora mesmo' : `${diffInSeconds} segundos atrás`
  }

  // Use absolute value for calculations to handle edge cases
  const absDiffInSeconds = Math.abs(diffInSeconds)

  const diffInMinutes = Math.floor(absDiffInSeconds / 60)
  if (diffInMinutes < 60) {
    return `${diffInMinutes} minuto${diffInMinutes === 1 ? '' : 's'} atrás`
  }

  const diffInHours = Math.floor(diffInMinutes / 60)
  if (diffInHours < 24) {
    return `${diffInHours} hora${diffInHours === 1 ? '' : 's'} atrás`
  }

  const diffInDays = Math.floor(diffInHours / 24)
  if (diffInDays < 30) {
    return `${diffInDays} dia${diffInDays === 1 ? '' : 's'} atrás`
  }

  const diffInMonths = Math.floor(diffInDays / 30)
  if (diffInMonths < 12) {
    return `${diffInMonths} mês${diffInMonths === 1 ? '' : 'es'} atrás`
  }

  const diffInYears = Math.floor(diffInMonths / 12)
  return `${diffInYears} ano${diffInYears === 1 ? '' : 's'} atrás`
}

/**
 * Format currency in Brazilian Real
 * @param value - Value to format
 * @returns Formatted currency string
 */
export function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value)
}

const BILLING_CYCLE_LABELS: Record<string, string> = {
  monthly: 'mês',
  quarterly: 'trimestre',
  semiannual: 'semestre',
  yearly: 'ano',
  annual: 'ano',
}

/**
 * Preço recorrente de um recurso: "R$ 49,90/mês".
 *
 * Devolve null quando não há preço registrado (ex.: IP incluso num produto de gateway),
 * para o chamador poder omitir a linha inteira em vez de exibir "R$ 0,00".
 */
export function formatRecurringPrice(
  unitPrice: number | null | undefined,
  billingCycle: string | null | undefined,
): string | null {
  if (unitPrice === null || unitPrice === undefined) return null
  const cycle = billingCycle ? BILLING_CYCLE_LABELS[billingCycle] || billingCycle : null
  return cycle ? `${formatCurrency(unitPrice)}/${cycle}` : formatCurrency(unitPrice)
}

/**
 * Truncate string with ellipsis
 * @param str - String to truncate
 * @param maxLength - Maximum length before truncation
 * @returns Truncated string
 */
export function truncateString(str: string, maxLength: number): string {
  if (str.length <= maxLength) return str
  return `${str.slice(0, maxLength - 3)}...`
}

/**
 * Format file size from bytes with appropriate unit
 * @param bytes - Size in bytes
 * @param binary - Use binary (1024) or decimal (1000) base (default: true)
 * @returns Formatted file size string
 */
export function formatFileSize(bytes: number, binary: boolean = true): string {
  if (bytes === 0) return '0 B'

  const base = binary ? 1024 : 1000
  const sizes = binary
    ? ['B', 'KiB', 'MiB', 'GiB', 'TiB', 'PiB']
    : ['B', 'KB', 'MB', 'GB', 'TB', 'PB']

  const i = Math.floor(Math.log(bytes) / Math.log(base))
  const size = bytes / Math.pow(base, i)

  return `${size.toFixed(1)} ${sizes[i]}`
}
